<template>
  <div class="ap-page">
    <div class="ap-filters">
      <v-select
        v-model="filters.partner_id"
        :items="partners"
        item-text="name"
        item-value="id"
        label="Partner"
        outlined
        dense
        hide-details
        :disabled="loading"
        class="ap-field ap-field--wide"
        @change="schedule = null"
      />

      <v-select
        v-model="filters.batch"
        :items="batchItems"
        item-text="label"
        item-value="value"
        label="Batch"
        outlined
        dense
        hide-details
        :disabled="loading || !filters.partner_id"
        class="ap-field"
        @change="schedule = null"
      />

      <v-btn
        color="primary"
        :disabled="!canSearch || loading"
        :loading="loading"
        @click="runSearch"
      >
        <v-icon small left>mdi-magnify</v-icon>
        Search
      </v-btn>

      <v-spacer />

      <v-btn-toggle v-model="view" mandatory dense class="ap-toggle">
        <v-btn small value="fee">Student Fee</v-btn>
        <v-btn small value="net">Revenue (excl GST)</v-btn>
      </v-btn-toggle>
      <v-btn
        small
        class="accent white--text"
        :disabled="!schedule"
        @click="downloadCsv"
      >
        <v-icon small left>mdi-download-outline</v-icon>
        Download
      </v-btn>
    </div>

    <!-- Which configuration produced these numbers -->
    <div v-if="rateSummary.length" class="ap-rates">
      <span class="ap-rates__label">Rates</span>
      <v-chip
        v-for="rate in rateSummary"
        :key="rate.label"
        x-small
        label
        outlined
        class="ap-rates__chip"
      >
        {{ rate.label }} {{ rate.text }}
      </v-chip>
      <v-chip
        v-if="!schedule.config.is_saved"
        x-small
        label
        color="warning"
        text-color="white"
        class="ap-rates__chip"
      >
        Using note defaults
      </v-chip>
      <v-spacer />
      <v-btn x-small text color="primary" @click="goToSetup">
        <v-icon x-small left>mdi-tune-variant</v-icon>
        Edit setup
      </v-btn>
    </div>

    <!-- States -->
    <div v-if="loading" class="ap-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="ap-state__text">Spreading the collections…</p>
    </div>

    <v-card v-else-if="!schedule" elevation="0" class="ap-state ap-state--card">
    </v-card>

    <v-card
      v-else-if="!schedule.months.length"
      outlined
      class="ap-state ap-state--card"
    >
      <v-icon size="44" color="grey lighten-1">mdi-cash-remove</v-icon>
      <h3 class="ap-state__title">Nothing collected</h3>
      <p class="ap-state__text">Nothing matches these filters.</p>
    </v-card>

    <template v-else>
      <!-- Headline -->
      <div class="ap-totals">
        <div class="ap-total">
          <span class="ap-total__label">Collected</span>
          <span class="ap-total__value">{{
            money(schedule.totals.collection_amount)
          }}</span>
          <span class="ap-total__meta"
            >{{ schedule.totals.collection_count }} payments</span
          >
        </div>
        <div class="ap-total">
          <span class="ap-total__label">Gross Revenue</span>
          <span class="ap-total__value">{{
            money(schedule.totals.gross_amount)
          }}</span>
          <span class="ap-total__meta">full service period</span>
        </div>
        <div class="ap-total">
          <span class="ap-total__label">GST</span>
          <span class="ap-total__value">{{
            money(schedule.totals.gst_amount)
          }}</span>
          <span class="ap-total__meta">on inclusive categories</span>
        </div>
        <div class="ap-total ap-total--net">
          <span class="ap-total__label">Net Revenue</span>
          <span class="ap-total__value">{{
            money(schedule.totals.net_amount)
          }}</span>
          <span class="ap-total__meta"
            >over {{ schedule.months.length }} months</span
          >
        </div>
      </div>

      <!-- Financial years -->
      <v-card outlined class="ap-card">
        <div class="ap-card__head">
          <div>
            <h3 class="ap-card__title">By financial year</h3>
            <p class="ap-card__sub">
              Deferred is collected but not yet earned.
            </p>
          </div>
        </div>
        <div class="ap-scroll">
          <table class="ap-table">
            <thead>
              <tr>
                <th class="ap-th">Financial Year</th>
                <th class="ap-th ap-th--num">Apportioned Fee</th>
                <th class="ap-th ap-th--num">Gross</th>
                <th class="ap-th ap-th--num">GST</th>
                <th class="ap-th ap-th--num">Net Revenue</th>
                <th class="ap-th ap-th--num">Deferred Fee</th>
                <th class="ap-th ap-th--num">Deferred Net</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fy in schedule.financial_years"
                :key="fy.financial_year"
              >
                <td class="ap-td ap-td--strong">{{ fy.financial_year }}</td>
                <td class="ap-td ap-td--num">
                  {{ money(fy.apportioned_fee) }}
                </td>
                <td class="ap-td ap-td--num">{{ money(fy.gross_amount) }}</td>
                <td class="ap-td ap-td--num">{{ money(fy.gst_amount) }}</td>
                <td class="ap-td ap-td--num ap-td--strong">
                  {{ money(fy.net_amount) }}
                </td>
                <td class="ap-td ap-td--num ap-td--muted">
                  {{ money(fy.deferred_fee) }}
                </td>
                <td class="ap-td ap-td--num ap-td--muted">
                  {{ money(fy.deferred_net) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- Month by month -->
      <v-card outlined class="ap-card">
        <div class="ap-card__head">
          <div>
            <h3 class="ap-card__title">Month by month</h3>
            <p class="ap-card__sub">
              {{ schedule.months[0].label }} –
              {{ schedule.months[schedule.months.length - 1].label }}
            </p>
          </div>
        </div>
        <div class="ap-scroll">
          <table class="ap-table">
            <thead>
              <tr>
                <th class="ap-th">Month</th>
                <th class="ap-th ap-th--num">Apportioned Fee</th>
                <th
                  v-for="name in categoryNames"
                  :key="name"
                  class="ap-th ap-th--num"
                >
                  {{ shortCategory(name) }}
                </th>
                <th class="ap-th ap-th--num">Net Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="month in schedule.months" :key="month.label">
                <td class="ap-td ap-td--strong">
                  {{ month.label }}
                  <span class="ap-sub">{{ month.financial_year }}</span>
                </td>
                <td class="ap-td ap-td--num">
                  {{ money(month.apportioned_fee) }}
                  <span class="ap-sub">{{ month.collection_count }} live</span>
                </td>
                <td
                  v-for="name in categoryNames"
                  :key="name"
                  class="ap-td ap-td--num"
                  :class="{ 'ap-td--zero': !categoryNet(month, name) }"
                >
                  {{
                    categoryNet(month, name)
                      ? money(categoryNet(month, name))
                      : "–"
                  }}
                </td>
                <td class="ap-td ap-td--num ap-td--strong">
                  {{ money(month.net_amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- What each group earned, and the rate that produced it -->
      <v-card outlined class="ap-card">
        <div class="ap-card__head">
          <div>
            <h3 class="ap-card__title">Group summary</h3>
            <p class="ap-card__sub">
              Effective rate is revenue ÷ fee apportioned, so it shows the rule
              each group was charged under.
            </p>
          </div>
        </div>

        <div class="ap-scroll">
          <table class="ap-table">
            <thead>
              <tr>
                <th class="ap-th">Group</th>
                <th class="ap-th ap-th--num">Rows</th>
                <th class="ap-th ap-th--num">Collected</th>
                <th class="ap-th ap-th--num">Revenue (excl GST)</th>
                <th class="ap-th ap-th--num">Effective rate</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="group in groupSummary.rows"
                :key="group.name"
                class="ap-row--click"
                :class="{ 'ap-row--on': poolFilter === group.name }"
                @click="
                  poolFilter = poolFilter === group.name ? null : group.name
                "
              >
                <td class="ap-td">{{ group.name }}</td>
                <td class="ap-td ap-td--num">{{ group.rows }}</td>
                <td class="ap-td ap-td--num">{{ money(group.collected) }}</td>
                <td class="ap-td ap-td--num ap-td--strong">
                  {{ money(group.revenue) }}
                </td>
                <td class="ap-td ap-td--num">{{ group.rate.toFixed(2) }}%</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="ap-tf">Total</td>
                <td class="ap-tf ap-td--num">{{ groupSummary.total.rows }}</td>
                <td class="ap-tf ap-td--num">
                  {{ money(groupSummary.total.collected) }}
                </td>
                <td class="ap-tf ap-td--num">
                  {{ money(groupSummary.total.revenue) }}
                </td>
                <td class="ap-tf"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>

      <!-- Student spread -->
      <v-card outlined class="ap-card">
        <div class="ap-card__head">
          <div>
            <h3 class="ap-card__title">
              {{
                view === "fee"
                  ? "Student fee spread"
                  : "Revenue spread (excl GST)"
              }}
            </h3>
            <p class="ap-card__sub">
              {{ schedule.students.length }} collections · month cells hold
              {{ view === "fee" ? "student fee" : "revenue excl GST" }}
            </p>
          </div>
          <div class="ap-card__tools">
            <v-select
              v-model="poolFilter"
              :items="poolFilterItems"
              item-text="text"
              item-value="value"
              label="Group"
              outlined
              dense
              hide-details
              class="ap-tool"
            />
            <v-text-field
              v-model="search"
              placeholder="USN, name, receipt"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="ap-tool"
            />
          </div>
        </div>

        <div v-if="switchingView" class="ap-state ap-state--spread">
          <v-progress-circular
            indeterminate
            color="primary"
            size="36"
            width="3"
          />
          <p class="ap-state__text mt-3">
            Building the
            {{ view === "fee" ? "student fee" : "revenue (excl GST)" }}
            spread…
          </p>
        </div>

        <div v-else class="ap-scroll ap-scroll--tall">
          <table class="ap-table ap-table--wide">
            <thead>
              <tr>
                <th class="ap-th ap-th--group ap-th--sticky" colspan="10"></th>
                <th class="ap-th ap-th--group ap-th--num" colspan="2">
                  Per month
                </th>
                <th
                  class="ap-th ap-th--group ap-th--num ap-th--band"
                  :colspan="schedule.months.length + 1"
                >
                  {{ view === "fee" ? "Student fee" : "Revenue (excl GST)" }}
                  spread over the service period
                </th>
              </tr>
              <tr>
                <th class="ap-th ap-th--sticky">USN</th>
                <th class="ap-th">Student</th>
                <th class="ap-th">Partner</th>
                <th class="ap-th">Programme</th>
                <th class="ap-th">Type</th>
                <th class="ap-th">Plan</th>
                <th class="ap-th">Receipt</th>
                <th class="ap-th ap-th--num">Amount</th>
                <th class="ap-th">Service Period</th>
                <th class="ap-th ap-th--num">Mths</th>
                <th class="ap-th ap-th--num">Fee / month</th>
                <th class="ap-th ap-th--num">Revenue / month</th>
                <th
                  v-for="month in schedule.months"
                  :key="month.label"
                  class="ap-th ap-th--num ap-th--month"
                >
                  {{ month.label }}
                </th>
                <th class="ap-th ap-th--num">Total</th>
              </tr>
            </thead>
            <tbody>
              <!-- Band rows and student rows in one flat list, so the body is a
                   single keyed v-for rather than a nested template. -->
              <tr
                v-for="row in spreadRows"
                :key="row.key"
                :class="{ 'ap-band-row': row.band }"
              >
                <template v-if="row.band">
                  <td class="ap-band" :colspan="13 + schedule.months.length">
                    <span class="ap-band__name">{{ row.name }}</span>
                    <span class="ap-band__meta">
                      {{ row.count }} collections · {{ money(row.total) }}
                    </span>
                  </td>
                </template>
                <template v-else>
                  <td class="ap-td ap-th--sticky">{{ row.usn_id }}</td>
                  <td class="ap-td">{{ row.student_name }}</td>
                  <td class="ap-td">{{ row.partner_name }}</td>
                  <td class="ap-td">{{ row.programme }}</td>
                  <td class="ap-td">{{ row.fee_type }}</td>
                  <td class="ap-td">{{ row.payment_type }}</td>
                  <td class="ap-td">{{ row.receipt_number }}</td>
                  <td class="ap-td ap-td--num">{{ money(row.amount) }}</td>
                  <td class="ap-td ap-td--nowrap">
                    {{ monthLabel(row.service_start) }} –
                    {{ monthLabel(row.service_end) }}
                  </td>
                  <td class="ap-td ap-td--num">{{ row.months }}</td>
                  <td class="ap-td ap-td--num">{{ money(row.monthly_fee) }}</td>
                  <td class="ap-td ap-td--num ap-td--strong">
                    {{ money(row.monthly_net) }}
                  </td>
                  <td
                    v-for="month in schedule.months"
                    :key="month.label"
                    class="ap-td ap-td--num ap-td--cell"
                    :class="{ 'ap-td--zero': !row.byMonth[month.label] }"
                  >
                    {{
                      row.byMonth[month.label]
                        ? money(row.byMonth[month.label])
                        : ""
                    }}
                  </td>
                  <td class="ap-td ap-td--num ap-td--strong">
                    {{ money(row.total) }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!switchingView && !visibleStudents.length" class="ap-state">
          <v-icon size="36" color="grey lighten-1">mdi-magnify-close</v-icon>
          <p class="ap-state__text mt-2">Nothing matches these filters.</p>
        </div>
      </v-card>
    </template>
  </div>
</template>

<script>
export default {
  name: "RevenueApportionment",

  data() {
    return {
      partners: [],
      programBatches: [],
      schedule: null,
      loading: false,

      view: "net",
      search: "",
      poolFilter: null,
      switchingView: false,
      switchTimer: null,
      filters: {
        partner_id: null,
        batch: null,
      },
    };
  },

  computed: {
    canSearch() {
      return Boolean(this.filters.partner_id && this.filters.batch);
    },

    batchItems() {
      const seen = new Map();

      this.programBatches.forEach((batch) => {
        const value = `${batch.year}-${batch.batch}`;
        if (seen.has(value)) return;
        seen.set(value, {
          value,
          label: `${batch.batch === 2 ? "Jul" : "Jan"} ${String(
            batch.year,
          ).slice(-2)}`,
          year: batch.year,
          batch: batch.batch,
        });
      });

      return [...seen.values()].sort(
        (a, b) => b.year - a.year || b.batch - a.batch,
      );
    },

    selectedBatch() {
      return this.batchItems.find((item) => item.value === this.filters.batch);
    },

    rateSummary() {
      const config = this.schedule?.config;
      if (!config) return [];

      const gst = (percentage, type) => {
        const value = Number(percentage) || 0;
        if (!value) return "";
        return type === "Inclusive"
          ? ` ÷ ${(1 + value / 100).toFixed(2)}`
          : ` + ${value}% GST`;
      };

      const rates = [
        {
          label: "Education Support",
          text: `${Number(config.education_rate)}%${gst(
            config.education_gst_percentage,
            config.education_gst_type,
          )}`,
        },
        {
          label: "Exam Support",
          text: `${Number(config.exam_rate)}%`,
        },
      ];

      rates.push(
        config.new_admission_included
          ? {
              label: "New Admission",
              text: `${Number(config.new_admission_rate)}%`,
            }
          : { label: "New Admission", text: "not included" },
      );

      return rates;
    },

    categoryNames() {
      const names = [];

      (this.schedule?.months || []).forEach((month) => {
        (month.categories || []).forEach((line) => {
          if (!names.includes(line.name)) names.push(line.name);
        });
      });

      return names;
    },

    groupOrder() {
      return [
        "New Admission",
        "1st Semester",
        "Higher Semester",
        "Other Fee",
        "Not charged",
      ];
    },

    poolFilterItems() {
      const present = new Set(
        (this.schedule?.students || []).map((row) => row.category_name),
      );

      const known = this.groupOrder.filter((name) => present.has(name));
      const rest = [...present].filter(
        (name) => !this.groupOrder.includes(name),
      );

      return [
        { text: "All groups", value: null },
        ...[...known, ...rest].map((name) => ({ text: name, value: name })),
      ];
    },

    spreadRows() {
      return this.groupedStudents.flatMap((group) => [
        {
          band: true,
          key: `g-${group.name}`,
          name: group.name,
          count: group.rows.length,
          total: group.total,
        },
        ...group.rows.map((row) => ({
          ...row,
          band: false,
          key: `${group.name}-${row.key}`,
        })),
      ]);
    },

    groupSummary() {
      const order = this.groupOrder;
      const groups = new Map();

      (this.schedule?.students || []).forEach((row) => {
        const name = row.category_name || "Other";
        if (!groups.has(name)) {
          groups.set(name, { name, rows: 0, collected: 0, fee: 0, revenue: 0 });
        }
        const group = groups.get(name);
        group.rows += 1;
        group.collected += Number(row.amount) || 0;
        group.fee += (Number(row.monthly_fee) || 0) * (Number(row.months) || 0);
        group.revenue += Number(row.total_net) || 0;
      });

      const rows = [...groups.values()]
        .map((group) => ({
          ...group,
          rate: group.fee ? (group.revenue / group.fee) * 100 : 0,
        }))
        .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));

      const total = rows.reduce(
        (acc, group) => ({
          rows: acc.rows + group.rows,
          collected: acc.collected + group.collected,
          revenue: acc.revenue + group.revenue,
        }),
        { rows: 0, collected: 0, revenue: 0 },
      );

      return { rows, total };
    },

    preparedStudents() {
      const useNet = this.view === "net";

      return (this.schedule?.students || []).map((row, index) => {
        const byMonth = {};
        let total = 0;

        (row.schedule || []).forEach((cell) => {
          const value = Number(useNet ? cell.net : cell.fee) || 0;
          byMonth[cell.label] = (byMonth[cell.label] || 0) + value;
          total += value;
        });

        return { ...row, key: index, byMonth, total };
      });
    },

    visibleStudents() {
      const term = (this.search || "").trim().toLowerCase();

      return this.preparedStudents.filter((row) => {
        if (this.poolFilter && row.category_name !== this.poolFilter)
          return false;
        if (!term) return true;
        return `${row.usn_id} ${row.student_name} ${row.receipt_number}`
          .toLowerCase()
          .includes(term);
      });
    },

    groupedStudents() {
      const order = this.groupOrder;
      const groups = new Map();

      this.visibleStudents.forEach((row) => {
        const name = row.category_name || "Other";
        if (!groups.has(name)) groups.set(name, { name, rows: [], total: 0 });
        const group = groups.get(name);
        group.rows.push(row);
        group.total += row.total;
      });

      return [...groups.values()].sort(
        (a, b) => order.indexOf(a.name) - order.indexOf(b.name),
      );
    },
  },

  watch: {
    view() {
      if (!this.schedule) return;

      clearTimeout(this.switchTimer);
      this.switchingView = true;

      this.switchTimer = setTimeout(() => {
        this.switchingView = false;
      }, 350);
    },
  },

  beforeDestroy() {
    clearTimeout(this.switchTimer);
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      {
        text: "Revenue Recognition",
        to: { name: "Revenue Apportionment Setup" },
      },
      { text: "Summary" },
    ]);
    this.initialise();
  },

  methods: {
    async initialise() {
      await Promise.all([this.getPartners(), this.getBatches()]);

      const query = this.$route.query;

      if (query.partner_id) {
        const partnerId = Number(query.partner_id);
        if (this.partners.some((partner) => partner.id === partnerId)) {
          this.filters.partner_id = partnerId;
        }
      }

      const requested =
        query.batch ||
        (query.intake_year && query.intake_no
          ? `${query.intake_year}-${query.intake_no}`
          : null);

      this.filters.batch =
        this.batchItems.find((item) => item.value === requested)?.value ||
        this.batchItems[0]?.value ||
        null;

      if (this.canSearch) await this.runSearch();
    },

    async getBatches() {
      try {
        const res = await this.$axios.get("/program_batches");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = res.data.data;
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async runSearch() {
      if (!this.canSearch) return;

      this.loading = true;
      try {
        const res = await this.$axios.get("/revenue_recognition", {
          params: {
            partner_id: this.filters.partner_id,
            intake_year: this.selectedBatch.year,
            intake_no: this.selectedBatch.batch,
          },
        });
        this.schedule = res.data.data;
      } catch (err) {
        this.schedule = null;
        this.notifyError(err, "Unable to build the apportionment");
      } finally {
        this.loading = false;
      }
    },

    goToSetup() {
      this.$router.push({
        name: "Revenue Apportionment Setup",
        query: { partner_id: this.filters.partner_id },
      });
    },

    downloadCsv() {
      const months = this.schedule.months.map((month) => month.label);
      const sheet = this.view === "net" ? "Revenue (excl GST)" : "Student fee";
      const blank = (count) => Array(count).fill("");

      const rows = [
        ["Partner", this.schedule.partner.name],
        ["Batch", this.selectedBatch ? this.selectedBatch.label : ""],
        ["Spread shown", sheet],
        [],

        ["GROUP SUMMARY"],
        ["Group", "Rows", "Collected", "Revenue (excl GST)", "Effective rate"],
        ...this.groupSummary.rows.map((group) => [
          group.name,
          group.rows,
          group.collected,
          group.revenue,
          `${group.rate.toFixed(2)}%`,
        ]),
        [
          "Total",
          this.groupSummary.total.rows,
          this.groupSummary.total.collected,
          this.groupSummary.total.revenue,
          "",
        ],
        [],

        ["BY FINANCIAL YEAR"],
        [
          "Financial Year",
          "Apportioned Fee",
          "Gross",
          "GST",
          "Net Revenue",
          "Deferred Fee",
          "Deferred Net",
        ],
        ...this.schedule.financial_years.map((fy) => [
          fy.financial_year,
          fy.apportioned_fee,
          fy.gross_amount,
          fy.gst_amount,
          fy.net_amount,
          fy.deferred_fee,
          fy.deferred_net,
        ]),
        [],

        ["MONTH BY MONTH"],
        [
          "Month",
          "Financial Year",
          "Apportioned Fee",
          ...this.categoryNames,
          "Net Revenue",
        ],
        ...this.schedule.months.map((month) => [
          month.label,
          month.financial_year,
          month.apportioned_fee,
          ...this.categoryNames.map((name) => this.categoryNet(month, name)),
          month.net_amount,
        ]),
        [],

        [`${sheet.toUpperCase()} SPREAD`],
        [
          "Group",
          "USN",
          "Application ID",
          "Student",
          "Partner",
          "Programme",
          "Fee Type",
          "Payment Type",
          "Receipt",
          "Transaction ID",
          "Collected On",
          "Amount",
          "Service Start",
          "Service End",
          "Months",
          "Fee / mo",
          "Revenue / mo",
          ...months,
          "Total",
        ],
        ...this.groupedStudents.flatMap((group) =>
          group.rows.map((row) => [
            group.name,
            row.usn_id,
            row.application_id,
            row.student_name,
            row.partner_name,
            row.programme,
            row.fee_type,
            row.payment_type,
            row.receipt_number,
            row.transaction_id,
            row.transaction_date,
            row.amount,
            row.service_start,
            row.service_end,
            row.months,
            row.monthly_fee,
            row.monthly_net,
            ...months.map((label) => row.byMonth[label] || ""),
            row.total,
          ]),
        ),

        [
          "TOTAL",
          ...blank(16),
          ...this.schedule.months.map((month) =>
            this.view === "net" ? month.net_amount : month.apportioned_fee,
          ),
          "",
        ],
      ];

      const csv = rows
        .map((row) =>
          row
            .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\r\n");

      const name = this.view === "net" ? "revenue_excl_gst" : "student_fee";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      link.download = `apportionment_${name}_${this.filters.batch}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    categoryNet(month, name) {
      const line = (month.categories || []).find(
        (entry) => entry.name === name,
      );
      return line ? Number(line.net_amount) || 0 : 0;
    },

    shortCategory(name) {
      return name
        .replace(" Service", "")
        .replace("Online Education Support", "Online Edu")
        .replace("Examination Support", "Exam")
        .replace("Academic Support", "Academic");
    },

    monthLabel(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN", {
        month: "short",
        year: "2-digit",
      });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.ap-page {
  --ap-line: #e4e7ec;
  --ap-ink: #101828;
  --ap-ink-2: #475467;
  --ap-ink-3: #98a2b3;
  padding: 0px 20px 40px;
}

.ap-filters {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
}

.ap-field {
  max-width: 170px;
  flex: 0 1 170px;
}

.ap-field--wide {
  max-width: 220px;
  flex: 0 1 220px;
}

.ap-toggle ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.ap-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.ap-state--card {
  margin-top: 12px;
}

.ap-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--ap-ink);
}

.ap-state__text {
  margin: 0;
  max-width: 600px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ap-ink-2);
}

.ap-totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.ap-total {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid var(--ap-line);
  border-radius: 10px;
}

.ap-total--net {
  border-left: 3px solid var(--v-primary-base, #00aff1);
}

.ap-total__label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ap-ink-2);
}

.ap-total__value {
  font-size: 19px;
  font-weight: 700;
  color: var(--ap-ink);
  font-variant-numeric: tabular-nums;
}

.ap-total__meta {
  font-size: 11.5px;
  color: var(--ap-ink-3);
}

.ap-card {
  margin-top: 12px;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.ap-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--ap-line);
}

.ap-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--ap-ink);
}

.ap-card__sub {
  margin: 3px 0 0;
  font-size: 12px;
  color: var(--ap-ink-2);
}

.ap-card__tools {
  display: flex;
  gap: 8px;
}

.ap-tool {
  max-width: 190px;
  flex: 0 1 190px;
}

.ap-scroll {
  overflow-x: auto;
}

.ap-scroll--tall {
  max-height: 620px;
  overflow-y: auto;
}

.ap-table {
  width: 100%;
  border-collapse: collapse;
}

.ap-table--wide {
  min-width: 1600px;
}

.ap-th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 8px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ap-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--ap-line);
  white-space: nowrap;
}

.ap-th--num {
  text-align: right;
}

.ap-th--month {
  min-width: 84px;
}

.ap-th--sticky {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #f9fafb;
}

td.ap-th--sticky {
  z-index: 1;
  background: #fff;
  font-weight: 600;
  color: var(--ap-ink);
}

.ap-td {
  padding: 7px 12px;
  font-size: 12px;
  color: var(--ap-ink-2);
  border-bottom: 1px solid #f4f5f7;
  white-space: nowrap;
}

.ap-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ap-td--strong {
  font-weight: 700;
  color: var(--ap-ink);
}

.ap-td--muted {
  color: var(--ap-ink-3);
}

.ap-td--nowrap {
  white-space: nowrap;
}

.ap-td--cell {
  background: #fcfdfe;
}

.ap-td--zero {
  background: #fff;
}

.ap-sub {
  display: block;
  font-size: 10.5px;
  font-weight: 400;
  color: var(--ap-ink-3);
}

.ap-band {
  padding: 6px 12px;
  background: #f4f6f8;
  border-top: 1px solid var(--ap-line);
  border-bottom: 1px solid var(--ap-line);
  position: sticky;
  left: 0;
}

.ap-band__name {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ap-ink);
}

.ap-band__meta {
  margin-left: 10px;
  font-size: 11px;
  color: var(--ap-ink-3);
  font-variant-numeric: tabular-nums;
}

@media (max-width: 600px) {
  .ap-page {
    padding: 12px 10px 40px;
  }

  .ap-filters {
    position: static;
  }

  .ap-field,
  .ap-field--wide,
  .ap-tool {
    max-width: none;
    flex: 1 1 100%;
  }
}

.ap-rates {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 12px;
  background: #f9fafb;
  border: 1px solid var(--ap-line, #e4e7ec);
  border-radius: 8px;
}

.ap-rates__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #667085;
}

.ap-rates__chip {
  font-variant-numeric: tabular-nums;
}

.ap-scroll--tall thead th {
  position: sticky;
  top: 0;
  z-index: 3;
}

.ap-scroll--tall thead th.ap-th--sticky {
  z-index: 4;
}

.ap-row--click {
  cursor: pointer;
}

.ap-row--click:hover {
  background: #f9fafb;
}

.ap-row--on {
  background: #eff8ff;
}

.ap-tf {
  padding: 10px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: #101828;
  background: #f9fafb;
  border-top: 1px solid var(--ap-line, #e4e7ec);
  white-space: nowrap;
}

.ap-th--group {
  height: 28px;
  padding-top: 5px;
  padding-bottom: 5px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 11px;
  color: #101828;
}

.ap-th--band {
  text-align: center;
  background: #eff8ff;
  border-bottom: 1px solid #b2ddff;
}

.ap-scroll--tall thead tr:first-child th {
  top: 0;
}

.ap-scroll--tall thead tr:nth-child(2) th {
  top: 28px;
}

.ap-state--spread {
  min-height: 220px;
}
</style>
